import { useCallback, useEffect, useState } from "react";
import { getEmployees } from "../Moduels/Employees/EmployeeApi";
import { sortEmployeesByLabel } from "../components/common/employeeUtils";

let cachedEmployees = null;
let loadPromise = null;
const listeners = new Set();

function notifyListeners() {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* ignore subscriber errors */
    }
  });
}

export function normalizeEmployeeList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.employees)) return data.employees;
  return [];
}

/** Drop soft-deleted / inactive rows even if a caller cached mixed data. */
function onlyActiveEmployees(rows) {
  return (rows || []).filter((e) => {
    if (e?.is_active === false) return false;
    const status = String(e?.status || "").toLowerCase();
    if (status === "inactive" || status === "terminated") return false;
    return true;
  });
}

async function fetchEmployeesOnce() {
  if (cachedEmployees) return cachedEmployees;
  if (!loadPromise) {
    loadPromise = getEmployees({ page: 1, page_size: 500, active_only: true })
      .then((data) => {
        cachedEmployees = sortEmployeesByLabel(
          onlyActiveEmployees(normalizeEmployeeList(data))
        );
        return cachedEmployees;
      })
      .catch((err) => {
        loadPromise = null;
        throw err;
      });
  }
  return loadPromise;
}

/**
 * Clear the shared employee cache so attendance / leave / payroll dropdowns
 * stay in sync after create/delete/deactivate. Mounted hooks re-fetch.
 */
export function invalidateEmployeesCache() {
  cachedEmployees = null;
  loadPromise = null;
  notifyListeners();
}

/** Cached active employee list for admin dropdowns across the app. */
export function useEmployees({ enabled = true } = {}) {
  const [employees, setEmployees] = useState(enabled ? cachedEmployees || [] : []);
  const [loading, setLoading] = useState(enabled && !cachedEmployees);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    cachedEmployees = null;
    loadPromise = null;
    if (!enabled) {
      setEmployees([]);
      setLoading(false);
      setError("");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const rows = await fetchEmployeesOnce();
      setEmployees(rows);
    } catch (e) {
      setError(e.message || "Failed to load employees");
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setEmployees([]);
      setLoading(false);
      setError("");
      return undefined;
    }
    if (cachedEmployees) {
      setEmployees(cachedEmployees);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    fetchEmployeesOnce()
      .then((rows) => {
        if (!cancelled) {
          setEmployees(rows);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e.message || "Failed to load employees");
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onInvalidate = () => {
      refresh();
    };
    listeners.add(onInvalidate);
    return () => {
      listeners.delete(onInvalidate);
    };
  }, [enabled, refresh]);

  return { employees, loading, error, refresh };
}
